import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAiScreenContext } from 'contexts/AiScreenContext';
import useFullScreenHandle from 'hooks/useFullScreenHandle';
import usePillarModulePermission from 'hooks/usePillarModulePermission';

import { REACT_FLOW, VALUE_CHAIN_AI_AGENT } from '../../../constants/general';
import {
  COMPANY_MAP_MODALS, COMPANY_MAP_TABS, type TCompanyMapModal, type TCompanyMapTab,
  type TValueChainStatus,
  USER_PILLAR_MODULES,
  VALUE_CHAIN_STATUS,
} from '../../../constants/interfaces';
import i18n from '../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/useApp';
import selectFullScreenActive from '../../../store/slices/general/selectors';
import {
  useGetAIDraftUnitsQuery,
  useGetValueChainMapDetailsQuery,
  useGetVCAgentSessionsQuery,
  useUpdateValueChainItemMutation,
} from '../../../store/slices/valueChainMap/apis/valueChainMapApi';
import { currentCompanyMapTabSelector } from '../../../store/slices/valueChainMap/selectors';
import { setValueChainMapState } from '../../../store/slices/valueChainMap/slice';
import hasAIDraftResults from '../../../utils/hasAIDraftResults';
import CompanyMapView from './CompanyMapView';

import type IBalanceSheetAssets from 'store/slices/valueChainMap/interfaces/IBalanceSheetAssets';
import type ICustomer from 'store/slices/valueChainMap/interfaces/ICustomer';
import type IProduct from 'store/slices/valueChainMap/interfaces/IProduct';
import type { IProfitCenter } from 'store/slices/valueChainMap/interfaces/IProfitCenter';
import type ISupplier from 'store/slices/valueChainMap/interfaces/ISupplier';
import type ILaunchpadDiscoverNavigationState from '../../UserPages/LaunchpadHub/interfaces/ILaunchpadDiscoverNavigationState';

function CompanyMap() {
  const [activeModal, setActiveModal] = useState<TCompanyMapModal | undefined>(undefined);
  const [currentZoom, setCurrentZoom] = useState<number>(REACT_FLOW.MIN_ZOOM);

  const [isProfitCentreModalOpen, setIsProfitCentreModalOpen] = useState<boolean>(false);
  const [selectedProfitCennter, setSelectedProfitCenter] = useState<IProfitCenter | undefined>();
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | undefined>();
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState<boolean>(false);
  const [selectedSupplier, setSelectedSupplier] = useState<ISupplier | undefined>();
  const [isBalanceSheetAssetsModalOpen, setIsBalanceSheetAssetsModalOpen] = useState<boolean>(false);
  const [selectedBalanceSheetAssets, setSelectedBalanceSheetAssets] = useState<IBalanceSheetAssets | undefined>();
  const [isInputProductModalOpen, setIsInputProductModalOpen] = useState<boolean>(false);
  const [selectedInputProduct, setSelectedInputProduct] = useState<IProduct | undefined>();

  const [currentTargetSwimlaneId, setCurrentTargetSwimlaneId] = useState<number | undefined>(undefined);
  const { valueChainMapId } = useParams();
  const location = useLocation();
  const launchpadDiscoverEntry = Boolean(
    (location.state as ILaunchpadDiscoverNavigationState | null)?.launchpadDiscover,
  );
  const fullScreenHandle = useFullScreenHandle();
  const dispatch = useAppDispatch();
  const isFullScreenActive = useAppSelector(selectFullScreenActive);
  const currentCompanyMapTab = useAppSelector(currentCompanyMapTabSelector);

  const {
    isAuditOrViewAccess,
    isLoading: isPillarModuleLoading,
  } = usePillarModulePermission(
    { pillarModuleKey: USER_PILLAR_MODULES.settings.dataSetsManagement },
  );

  const { data, isLoading } = useGetValueChainMapDetailsQuery({ chainId: valueChainMapId || '' });
  const { data: vcAgentSessions } = useGetVCAgentSessionsQuery(
    { valueChainMapId: valueChainMapId || '' },
    { skip: !valueChainMapId },
  );
  const existingSession = vcAgentSessions?.data?.find((sessionItem) => (
    sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE && sessionItem.isActive
  )) || vcAgentSessions?.data?.find(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE,
  );
  const isExistingSessionDraftReviewReady = existingSession?.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
    || existingSession?.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
  const { data: draftUnits } = useGetAIDraftUnitsQuery({
    sessionId: existingSession?.id || 0,
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !existingSession?.id || !valueChainMapId || !isExistingSessionDraftReviewReady,
  });
  const hasDraftResults = useMemo(
    () => hasAIDraftResults(draftUnits?.data),
    [draftUnits?.data],
  );
  const isStatusLow = data?.data.status === VALUE_CHAIN_STATUS.archived
    || data?.data.status === VALUE_CHAIN_STATUS.requiresRework
    || data?.data.status === VALUE_CHAIN_STATUS.approved;
  const isAccessDisabled = isAuditOrViewAccess || isStatusLow;

  const [updateValueChainItem] = useUpdateValueChainItemMutation();

  // The route already names the map. This adds what only the screen knows: which map it is by
  // name. The tab the user is on declares itself as a sub-screen.
  useAiScreenContext({
    entities: valueChainMapId
      ? [{ type: 'valueChainMap', id: valueChainMapId, label: data?.data?.name }]
      : [],
  });

  const onTabHandler = (tab: TCompanyMapTab) => {
    dispatch(setValueChainMapState({ currentCompanyMapTab: tab }));
  };

  useEffect(() => {
    if (launchpadDiscoverEntry) {
      dispatch(setValueChainMapState({ currentCompanyMapTab: COMPANY_MAP_TABS.VALUE_CHAIN_MAP }));
    }
  }, [dispatch, launchpadDiscoverEntry]);

  const onRenameHandler = () => {
    setActiveModal(COMPANY_MAP_MODALS.RENAME);
  };

  const closeModal = () => {
    setActiveModal(undefined);
  };

  const onRenameSubmit = (name: string) => {
    updateValueChainItem({ chainId: Number(valueChainMapId), name })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.valueChainMapHasBeenRenamed')));
    setActiveModal(undefined);
  };

  const onChangeStatus = (status: TValueChainStatus) => {
    updateValueChainItem({ chainId: Number(valueChainMapId), status })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.statusHasBeenUpdated')));
  };

  const onMoveEnd = (zoom: number) => {
    setCurrentZoom(zoom);
  };

  const onMaximizeHandler = () => {
    const { exit, enter } = fullScreenHandle;
    if (isFullScreenActive) {
      exit();
    } else {
      enter();
    }
  };

  const onCloseProfitCentreModal = () => {
    setIsProfitCentreModalOpen(false);
    setCurrentTargetSwimlaneId(undefined);
    setIsCustomerModalOpen(false);
    setIsSupplierModalOpen(false);
    setIsBalanceSheetAssetsModalOpen(false);
    setIsInputProductModalOpen(false);
  };

  const loader = isPillarModuleLoading && isLoading;

  return (
    <CompanyMapView
      currentTab={currentCompanyMapTab}
      onTabHandler={onTabHandler}
      activeModal={activeModal}
      onRenameHandler={onRenameHandler}
      closeModal={closeModal}
      onRenameSubmit={onRenameSubmit}
      valueChainMapId={valueChainMapId}
      currentZoom={currentZoom}
      onMoveEnd={onMoveEnd}
      data={data?.data}
      isLoading={loader}
      onChangeStatus={onChangeStatus}
      isProfitCentreModalOpen={isProfitCentreModalOpen}
      closeProfitCenterModal={onCloseProfitCentreModal}
      openProfitCenterModal={() => setIsProfitCentreModalOpen(true)}
      isFullScreenActive={isFullScreenActive}
      onMaximizeHandler={onMaximizeHandler}
      isAccessDisabled={isAccessDisabled}
      selectedProfitCennter={selectedProfitCennter}
      setSelectedProfitCenter={setSelectedProfitCenter}
      setCurrentTargetSwimlaneId={setCurrentTargetSwimlaneId}
      currentTargetSwimlaneId={currentTargetSwimlaneId}
      openCustomerModal={() => setIsCustomerModalOpen(true)}
      setSelectedCustomer={setSelectedCustomer}
      isCustomerModalOpen={isCustomerModalOpen}
      selectedCustomer={selectedCustomer}
      openSupplierModal={() => setIsSupplierModalOpen(true)}
      setSelectedSupplier={setSelectedSupplier}
      isSupplierModalOpen={isSupplierModalOpen}
      selectedSupplier={selectedSupplier}
      openBalanceSheetAssetsModal={() => setIsBalanceSheetAssetsModalOpen(true)}
      setSelectedBalanceSheetAssets={setSelectedBalanceSheetAssets}
      isBalanceSheetAssetsModalOpen={isBalanceSheetAssetsModalOpen}
      selectedBalanceSheetAssets={selectedBalanceSheetAssets}
      openInputProductModal={() => setIsInputProductModalOpen(true)}
      setSelectedInputProduct={setSelectedInputProduct}
      isInputProductModalOpen={isInputProductModalOpen}
      selectedInputProduct={selectedInputProduct}
      existingSession={existingSession}
      hasDraftResults={hasDraftResults}
      launchpadDiscoverEntry={launchpadDiscoverEntry}
    />
  );
}

export default CompanyMap;
